import axios from 'axios'

// Use proxy routes for development, production URLs for production
// Check for development mode using multiple indicators
const isDevelopment = import.meta.env.DEV || import.meta.env.MODE === 'development' || window.location.hostname === 'localhost'
const API_BASE_URL = isDevelopment ? '/api/v1' : (import.meta.env.VITE_API_URL || 'https://api.cognavia.ai/api/v1')
const AUTH_API_BASE_URL = isDevelopment ? '/api' : (import.meta.env.VITE_API_URL?.replace('/v1', '') || 'https://api.cognavia.ai/api')

// Debug logging
console.log('🔧 API Configuration Debug:')
console.log('- import.meta.env.DEV:', import.meta.env.DEV)
console.log('- import.meta.env.MODE:', import.meta.env.MODE)
console.log('- window.location.hostname:', window.location.hostname)
console.log('- isDevelopment:', isDevelopment)
console.log('- API_BASE_URL:', API_BASE_URL)
console.log('- AUTH_API_BASE_URL:', AUTH_API_BASE_URL)

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000, // 10 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
})

export const authApi = axios.create({
  baseURL: AUTH_API_BASE_URL,
  timeout: 10000, // 10 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
})

// Customer-only API instance - only uses customer tokens
export const customerApi = axios.create({
  baseURL: AUTH_API_BASE_URL,
  timeout: 60000, // 60 second timeout (for AI/agent operations)
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor for both API instances
const addAuthInterceptor = (apiInstance) => {
  apiInstance.interceptors.request.use(
    (config) => {
      // Try admin token first, then regular token
      const adminToken = localStorage.getItem('admin_auth_token')
      const userToken = localStorage.getItem('auth_token')
      const token = adminToken || userToken
      
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  apiInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Don't auto-redirect on 401 during login attempts
      if (error.response?.status === 401 && !error.config.url?.includes('/auth/login')) {
        // Check if we have admin or user token to determine redirect
        const adminToken = localStorage.getItem('admin_auth_token')
        const userToken = localStorage.getItem('auth_token')

        if (adminToken) {
          localStorage.removeItem('admin_auth_token')
          localStorage.removeItem('user_data')
          window.location.href = '/admin-login'
        } else if (userToken) {
          localStorage.removeItem('auth_token')
          localStorage.removeItem('user_data')
          window.location.href = '/login'
        }
      }
      return Promise.reject(error)
    }
  )
}

// Customer-only auth interceptor - only uses customer tokens
const addCustomerAuthInterceptor = (apiInstance) => {
  apiInstance.interceptors.request.use(
    (config) => {
      // Only use customer token, never admin token
      const userToken = localStorage.getItem('auth_token')

      if (userToken) {
        config.headers.Authorization = `Bearer ${userToken}`
      } else {
        // No customer token - this should be handled by route guards
        console.warn('Customer API called without customer token')
      }
      return config
    },
    (error) => Promise.reject(error)
  )

  apiInstance.interceptors.response.use(
    (response) => response,
    (error) => {
      // Customer-specific error handling
      if (error.response?.status === 401) {
        console.warn('Customer API 401 error - redirecting to customer login')
        localStorage.removeItem('auth_token')
        localStorage.removeItem('user_data')
        window.location.href = '/login'
      }
      return Promise.reject(error)
    }
  )
}

addAuthInterceptor(api)
addAuthInterceptor(authApi)
addCustomerAuthInterceptor(customerApi)

// API service methods
export const authAPI = {
  login: (email, password) => api.post('/admin/auth/login', { email, password }),
  getCurrentUser: () => api.get('/admin/auth/me'),
  logout: () => api.post('/admin/auth/logout'),
}

export const registrationsAPI = {
  getAll: (params) => api.get('/admin/registrations', { params }),
  approve: (id, notes) => api.put(`/admin/registrations/${id}/approve`, { admin_notes: notes }),
  reject: (id, reason, notes) => api.put(`/admin/registrations/${id}/reject`, { 
    rejection_reason: reason, 
    admin_notes: notes 
  }),
  terminate: (id, reason, notes) => api.put(`/admin/registrations/${id}/terminate`, { 
    termination_reason: reason, 
    admin_notes: notes 
  }),
  delete: (id) => api.delete(`/admin/registrations/${id}`),
  getById: (id) => api.get(`/admin/registrations/${id}`),
}

export const tenantsAPI = {
  getAll: (params) => api.get('/admin/tenants', { params }),
  getById: (id) => api.get(`/admin/tenants/${id}`),
  update: (id, data) => api.put(`/admin/tenants/${id}`, data),
  suspend: (id, reason) => api.post(`/admin/tenants/${id}/suspend`, { reason }),
  reactivate: (id) => api.post(`/admin/tenants/${id}/reactivate`),
  delete: (id) => api.delete(`/admin/tenants/${id}`),
  getStats: (id) => api.get(`/admin/tenants/${id}/stats`),
}

export const analyticsAPI = {
  getDashboard: () => api.get('/admin/analytics/dashboard'), // Admin dashboard endpoint
  getRegistrationStats: (period) => api.get('/admin/analytics/registrations', { 
    params: { period } 
  }),
  getTenantStats: (period) => api.get('/admin/analytics/tenants', { 
    params: { period } 
  }),
  getChatStats: (period) => api.get('/admin/analytics/chat', { 
    params: { period } 
  }),
}

export const referralsAPI = {
  getAll: (params) => api.get('/admin/referrals/', { params }),
  getById: (id) => api.get(`/admin/referrals/${id}`),
  approve: (id, notes) => api.put(`/admin/referrals/${id}/approve`, { admin_notes: notes }),
  reject: (id, reason, notes) => api.put(`/admin/referrals/${id}/reject`, {
    rejection_reason: reason,
    admin_notes: notes
  }),
}

export const trialAPI = {
  getDiagnostic: () => api.get('/admin/registrations/trial-reminder/diagnostic'),
  getEmailLog: () => api.get('/admin/registrations/trial-reminder/email-log'),
  triggerCron: () => api.post('/admin/registrations/trial-reminder/trigger'),
  sendManual: (userId, daysRemaining) => api.post('/admin/registrations/trial-reminder/send-manual', { user_id: userId, days_remaining: daysRemaining }),
  sendTest: (email, name, daysRemaining) => api.post('/admin/registrations/trial-reminder/test', { email, name, days_remaining: daysRemaining }),
}

export const usersAPI = {
  getAll: (params) => api.get('/admin/registrations/users', { params }),
  getById: (id) => api.get(`/admin/registrations/users/${id}`),
  activate: (id, notes) => api.put(`/admin/registrations/users/${id}/activate`, { admin_notes: notes }),
  deactivate: (id, notes) => api.put(`/admin/registrations/users/${id}/deactivate`, { admin_notes: notes }),
  delete: (id) => api.delete(`/admin/registrations/users/${id}`),
  toggleVoice: (userId, enabled) => api.post('/admin/user-settings/toggle-user-voice', { user_id: userId, enabled }),
  updatePlan: (id, plan) => api.put(`/admin/registrations/users/${id}/update-plan`, { subscription_plan: plan }),
  extendTrial: (id, days, notes) => api.put(`/admin/registrations/users/${id}/extend-trial`, { days, admin_notes: notes }),
}

// Multilingual chatbot — admin toggle per customer
export const multilingualAPI = {
  // Returns { customers: [...], total, supported_languages: {...} }
  getCustomers: (params) => api.get('/admin/multilingual/customers', { params }),
  // Toggle multilingual on/off for a specific customer
  toggle: (userId, enabled) =>
    api.put(`/admin/multilingual/customers/${userId}/toggle`, { enabled }),
}

// Customer auth API methods
authApi.login = (email, password) => authApi.post('/auth/login', { email, password })
authApi.register = (userData) => authApi.post('/auth/register/individual', userData)
authApi.verifyOtp = (otpData) => authApi.post('/auth/verify-otp-new', otpData)
authApi.getApiKey = () => authApi.get('/auth/api-key')
authApi.regenerateApiKey = () => authApi.post('/auth/regenerate-api-key')
authApi.forgotPassword = (data) => authApi.post('/auth/forgot-password', data)
authApi.verifyResetOtp = (data) => authApi.post('/auth/verify-reset-otp', data)
authApi.resetPassword = (data) => authApi.post('/auth/reset-password', data)
authApi.toggleVoice = (enabled) => authApi.post('/auth/toggle-voice', { enabled })
authApi.getVoiceSettings = () => authApi.get('/auth/voice-settings')
authApi.getWidgetConfig = () => authApi.get('/auth/widget-config')
authApi.updateWidgetConfig = (config) => authApi.put('/auth/widget-config', config)
authApi.redeemReferral = (code) => authApi.post('/auth/redeem-referral', { code })

// Payments API
export const paymentsAPI = {
  getPlans: () => authApi.get('/customer/payments/plans'),
  createOrder: (planType) => customerApi.post('/customer/payments/create-order', { plan_type: planType }),
  verifyPayment: (data) => customerApi.post('/customer/payments/verify', data),
  getHistory: () => customerApi.get('/customer/payments/history'),
  getSubscription: () => customerApi.get('/customer/payments/subscription'),
}

export default api